/**
 * Phase 19 — rules as stored (inside hourOptimizerJson / textRankingJson /
 * the gateway ranking): a tolerant parser for readers (a malformed rule is
 * dropped, never crashes a plan) and the validator the API runs on writes
 * (each problem in words). A surface's check says whether its rules may test
 * hour tags (hour rules only); by default they may not.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FLOW_FORMULA_METRIC_IDS } from "../../formula/catalog";
import { DROP_GEN_HOUR_DATE_RANGES } from "../hour-profile.types";
import { floorAsCondition } from "./default-rules";
import { rankingNodeErrors, usesRanking } from "./ranking/score-rank";
import {
  DROP_GEN_FIRST_RANGE,
  DROP_GEN_NO_TAGS_CHECK,
  DROP_GEN_RULE_LIMITS as LIMITS,
  type DropGenRule,
  type DropGenRuleCheck,
  type DropGenRuleNode,
  type DropGenRuleOp,
} from "./rules.types";
import { scopeErrors } from "./scope";
import { tagNodeErrors } from "./tags/tag-node";

const OPS: DropGenRuleOp[] = ["<", "<=", "=", "!=", ">", ">="];
const METRICS = new Set(FLOW_FORMULA_METRIC_IDS);
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A whole-day preset, "custom" with both bounds (the reports' own ranges), or the formula's first range. */
function rangeOk(r: Record<string, unknown>): boolean {
  if (r.dateRange === DROP_GEN_FIRST_RANGE) return true;
  if (r.dateRange === "custom") return DAY.test(String(r.dateRangeCustomFrom)) && DAY.test(String(r.dateRangeCustomTo));
  return (DROP_GEN_HOUR_DATE_RANGES as readonly string[]).includes(r.dateRange as string);
}

function nodeErrors(n: unknown, path: string, depth: number, check: DropGenRuleCheck): string[] {
  if (typeof n !== "object" || n === null) return [`${path}: not a rule node`];
  const r = n as Record<string, unknown>;
  if (depth > LIMITS.depth) return [`${path}: nested deeper than ${LIMITS.depth}`];
  if (r.kind === "tag") return tagNodeErrors(r, path, check);
  if (r.kind === "condition") {
    const errs: string[] = [];
    const allowed = check.metrics ? new Set(check.metrics) : METRICS;
    if (typeof r.metric !== "string" || !allowed.has(r.metric)) errs.push(`${path}: "${String(r.metric)}" is not a formula metric`);
    if (!OPS.includes(r.op as DropGenRuleOp)) errs.push(`${path}: unknown operator ${String(r.op)}`);
    if (typeof r.value !== "number" || !Number.isFinite(r.value)) errs.push(`${path}: the value must be a number`);
    return errs;
  }
  if (r.kind === "ranking") return rankingNodeErrors(r, path);
  if (r.kind === "not") return nodeErrors(r.child, `${path}.not`, depth + 1, check);
  if (r.kind !== "group") return [`${path}: unknown node kind`];
  if (r.op !== "AND" && r.op !== "OR") return [`${path}: a group is ALL (AND) or ANY (OR)`];
  if (!Array.isArray(r.children) || r.children.length > LIMITS.conditions) return [`${path}: 0 to ${LIMITS.conditions} conditions`];
  return r.children.flatMap((c, i) => nodeErrors(c, `${path}[${i}]`, depth + 1, check));
}

/** Every problem of a rule list, in words — empty when valid. */
export function ruleErrors(
  json: unknown,
  actions: readonly string[],
  check: DropGenRuleCheck = DROP_GEN_NO_TAGS_CHECK,
): string[] {
  if (json === undefined || json === null) return [];
  if (!Array.isArray(json)) return ["rules must be a list"];
  if (json.length > LIMITS.rules) return [`at most ${LIMITS.rules} rules`];
  return json.flatMap((r: Record<string, unknown>, i) => {
    const at = `rule ${i + 1}`;
    const errs: string[] = [];
    if (typeof r?.id !== "string" || !r.id) errs.push(`${at}: an id is required`);
    if (typeof r?.name !== "string" || r.name.length > 80) errs.push(`${at}: a name of up to 80 characters`);
    if (!actions.includes(r?.action as string)) errs.push(`${at}: the action must be one of ${actions.join(", ")}`);
    if (!r || !rangeOk(r)) errs.push(`${at}: a whole-day date range (or custom from / to)`);
    if (r?.action === "reduce_slots") {
      const config = r.reduceSlots as Record<string, unknown> | undefined;
      if (!config || !Number.isSafeInteger(config.count) || Number(config.count) < 1 || !Number.isSafeInteger(config.minRemaining) || Number(config.minRemaining) < 0)
        errs.push(`${at}: reduce slots needs an integer count ≥ 1 and minimum remaining ≥ 0`);
    } else if (r?.reduceSlots !== undefined) errs.push(`${at}: reduceSlots is only for the Reduce slots action`);
    const nodeErrs = nodeErrors(r?.when, `${at}.when`, 1, check);
    if (nodeErrs.length === 0 && r?.action === "exclude" && usesRanking(r.when as DropGenRuleNode))
      errs.push(`${at}: an Exclude rule decides what is ranked — it cannot read the score ranking`);
    return [...errs, ...nodeErrs, ...(r ? scopeErrors(r, at, check) : [])];
  });
}

/** Stored JSON → the usable rules (a malformed one is dropped); an old rule's own floor becomes its first condition. */
export function parseRules<A extends string>(
  json: unknown,
  actions: readonly A[],
  check: DropGenRuleCheck = DROP_GEN_NO_TAGS_CHECK,
): DropGenRule<A>[] {
  if (!Array.isArray(json)) return [];
  return json
    .map((r) => (r && typeof r === "object" ? floorAsCondition(r as Record<string, unknown>) : r))
    .filter((r) => ruleErrors([r], actions, check).length === 0)
    .map((r) => ({ ...r, enabled: r.enabled !== false }) as DropGenRule<A>);
}

let seq = 0;
/** A new, empty rule — one condition to start from. */
export function newRule<A extends string>(action: A, dateRange: string, condition: Omit<Extract<DropGenRuleNode, { kind: "condition" }>, "kind">): DropGenRule<A> {
  seq += 1;
  return {
    id: `r${Date.now().toString(36)}${seq}`,
    name: "New rule",
    enabled: true,
    dateRange,
    when: { kind: "group", op: "AND", children: [{ kind: "condition", ...condition }] },
    action,
  };
}
